// The language rules every translation path shares (TGCLOUD-1893): which codes
// are English, how a `{{placeholder}}` is filled, the pseudo-locale, and the
// tag `Intl` takes for a language. Pure, so the hook, the i18next backend, the
// date helpers and the MUI locale cannot disagree.

// The pseudo-locale: a language whose `t()` returns the text accented and
// bracketed, with no request to anything. Whatever is still plain English on a
// screen in it never went through `t()`. Offered in the language menu outside
// production builds only.
export const PSEUDO_LANGUAGE = 'pseudo';

export const isPseudoAvailable = () => import.meta.env?.MODE !== 'production';

// English is the source language: every key IS its English text. The menu
// offers four English variants (en, en_GB, en_AU, en_CA); none of them is
// translated, so none of them may send a string to Google. `dev` is i18next's
// own placeholder language before one is chosen.
export const isEnglish = (lang) =>
  !lang || lang === 'en' || lang === 'dev' || /^en[-_]/i.test(lang);

// `{{name}}`: letters and digits only. A key's '.' is stored as '_' (a Mongo
// field name cannot hold a dot), and the translation's '_' is turned back into
// '.', so a placeholder name holding either would not survive the round trip.
export const PLACEHOLDER_RE = /\{\{\s*([A-Za-z][A-Za-z0-9]*)\s*\}\}/g;

/**
 * Fill each `{{name}}` in `text` with `vars[name]`. A placeholder with no value
 * is left as written, so a missing variable shows rather than vanishes.
 *
 * @param {string} text
 * @param {Record<string, string|number|null|undefined>} [vars]
 * @returns {string}
 */
export const interpolate = (text, vars) => {
  if (!vars) return text;
  return text.replace(PLACEHOLDER_RE, (match, name) =>
    Object.prototype.hasOwnProperty.call(vars, name)
      ? String(vars[name] ?? '')
      : match
  );
};

/**
 * What t() renders in English: the text with its `{{placeholders}}` filled.
 * The default for a pure helper that takes `t` from the component calling it
 * (`getRecordTitle(record, t)`), so a caller with no language still gets
 * finished English.
 *
 * @param {string} text
 * @param {Record<string, string|number|null|undefined>} [vars]
 * @returns {string}
 */
export const english = (text, vars) =>
  text === null || text === undefined || text === ''
    ? ''
    : interpolate(String(text), vars);

/** The placeholder names in `text`, sorted, one per occurrence. */
export const placeholdersOf = (text) =>
  [...String(text).matchAll(PLACEHOLDER_RE)].map((m) => m[1]).sort();

// Each ASCII letter and its accented stand-in, position for position. Every
// stand-in is one code point.
const PLAIN = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
const ACCENTED = [...'àƀçďéƒĝĥîĵķļɱñöƥʠŕšţûṽŵẋýžÀƁÇĎÉƑĜĤÎĴĶĻṀÑÖƤǪŔŠŢÛṼŴẊÝŽ'];

/**
 * The pseudo-locale's rendering of `text`: every ASCII letter accented, the
 * whole wrapped in ⟦ ⟧, each `{{placeholder}}` kept so it is still filled.
 */
export const pseudoLocalise = (text) =>
  `⟦${String(text).replace(/\{\{[^}]*\}\}|[A-Za-z]/g, (m) =>
    m.length > 1 ? m : ACCENTED[PLAIN.indexOf(m)]
  )}⟧`;

/**
 * The BCP 47 tag `Intl` takes for a language code from the menu: `en_GB` is
 * written `en-GB`. The pseudo-locale formats as English, then is accented.
 * A code `Intl` rejects falls back to English.
 */
export const intlLocale = (lang) => {
  if (!lang || lang === PSEUDO_LANGUAGE || lang === 'dev') return 'en';
  const tag = String(lang).replace(/_/g, '-');
  try {
    return Intl.getCanonicalLocales(tag)[0] || 'en';
  } catch {
    return 'en';
  }
};
